import React, { useState, useEffect } from 'react';
import MarkdownPost from '../components/MarkdownPost';
import '../styles/BlogPage.css';

const BlogPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(false); // Changed to false since we're using static data
    const [currentPost, setCurrentPost] = useState(null); // For viewing individual posts

    // Static blog posts data
    const staticBlogPosts = [
        {
            id: 'evo-llm-1',
            title: 'The New Scientific Method: Evolutionary Search and Mathematical Discovery with Large Language Models',
            excerpt: 'Exploring how Large Language Models are revolutionizing scientific discovery through evolutionary search principles, creating a new paradigm for mathematical research and automated hypothesis generation.',
            category: 'generative-ai',
            date: '2025-07-15',
            readTime: '10 min read',
            tags: ['LLMs', 'Evolutionary Search', 'Scientific Discovery', 'Mathematical Research'],
            url: '/posts/evo_llm.md', // Changed to .md for Markdown support
            type: 'markdown' // Add type to distinguish between HTML and Markdown
        }
    ];

    useEffect(() => {
        // For now, just use static data
        setBlogPosts(staticBlogPosts);
        setLoading(false);

        // Uncomment this when you want to fetch from Notion again
        /*
        const fetchBlogPosts = async () => {
            try {
                const response = await fetch('/.netlify/functions/get-blog-posts');
                const data = await response.json();

                const notionPosts = data.results.map(post => ({
                    id: post.id,
                    title: post.properties.title.title[0].plain_text,
                    excerpt: post.properties.excerpt.rich_text[0].plain_text,
                    category: post.properties.category.select.name,
                    date: post.properties.date.date.start,
                    readTime: post.properties.readTime.rich_text[0].plain_text,
                    tags: post.properties.tags.multi_select.map(tag => tag.name),
                }));

                // Combine static posts with Notion posts
                setBlogPosts([...staticBlogPosts, ...notionPosts]);
            } catch (error) {
                console.error('Error fetching blog posts:', error);
                // Fallback to static posts only
                setBlogPosts(staticBlogPosts);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogPosts();
        */
    }, []);

    const categories = [
        { id: 'all', name: 'All Articles', icon: '🎯' },
        { id: 'deep-learning', name: 'Deep Learning', icon: '🧠' },
        { id: 'nlp', name: 'NLP', icon: '💬' },
        { id: 'computer-vision', name: 'Computer Vision', icon: '👁️' },
        { id: 'reinforcement-learning', name: 'Reinforcement Learning', icon: '🎮' },
        { id: 'mlops', name: 'MLOps', icon: '⚙️' },
        { id: 'generative-ai', name: 'Generative AI', icon: '🎨' }
    ];
    
    const filteredPosts = selectedCategory === 'all'
        ? blogPosts
        : blogPosts.filter(post => post.category === selectedCategory);

    const handleReadMore = (post) => {
        if (post.type === 'markdown') {
            // Show the markdown post in the component
            setCurrentPost(post);
        } else if (post.url) {
            // Open HTML posts in new tab
            window.open(post.url, '_blank');
        }
    };

    const handleBackToBlog = () => {
        setCurrentPost(null);
    };

    // If viewing a specific post, show the MarkdownPost component
    if (currentPost) {
        return (
            <MarkdownPost 
                postUrl={currentPost.url}
                title={currentPost.title}
                onBack={handleBackToBlog}
            />
        );
    }
    
    return (
        <div className="blog-page">
            <div className="blog-header">
                <h1>ML & AI Blog</h1>
                <p>Exploring the frontiers of Machine Learning and Artificial Intelligence</p>
            </div>
    
            <div className="category-filter">
                {categories.map(category => (
                    <button
                        key={category.id}
                        className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category.id)}
                    >
                        <span className="category-icon">{category.icon}</span>
                        {category.name}
                    </button>
                ))}
            </div>
    
            {loading ? (
                <div className="loading-container">
                    <p>Loading posts...</p>
                </div>
            ) : (
                <div className="blog-grid">
                    {filteredPosts.length > 0 ? (
                        filteredPosts.map(post => (
                            <article key={post.id} className="blog-card">
                                <div className="blog-card-header">
                                    <div className="blog-meta">
                                        <span className="blog-date">{new Date(post.date).toLocaleDateString('en-US', { 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric' 
                                        })}</span>
                                        <span className="blog-read-time">{post.readTime}</span>
                                    </div>
                                </div>
        
                                <h2 className="blog-title">{post.title}</h2>
                                <p className="blog-excerpt">{post.excerpt}</p>
        
                                <div className="blog-tags">
                                    {post.tags.map((tag, index) => (
                                        <span key={index} className="blog-tag">{tag}</span>
                                    ))}
                                </div>
        
                                <button 
                                    className="read-more-btn"
                                    onClick={() => handleReadMore(post)}
                                >
                                    Read Article →
                                </button>
                            </article>
                        ))
                    ) : (
                        <div className="no-posts">
                            <p>No posts found in this category.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BlogPage;